import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AllocationTier,
    IncreaseAllocationModalProps,
    IncreaseAllocationStage,
    PoolProps,
} from '@decub8/ui';
import { commify, formatUnits } from '@ethersproject/units';
import dayjs from 'dayjs';

import { getTokenPrice } from '@src/components/Staking/Analytics/util';
import {
    filterPoolsByStakingVersions,
    getPoolItem,
} from '@src/components/Staking/StakingPools/hooks';
import { CONTRACT, DEFAULT_CHAIN_ID } from '@src/config';
import { setStakingModalOpen } from '@src/features/layout';
import {
    setCurrentPool,
    setDepositAmount,
    setToken1DepositAmount,
    updatePool,
} from '@src/features/staking';
import { useGlobalContext } from '@src/hooks/useGlobalContext';
import { useUserAllocationMultiplier } from '@src/hooks/useUserAllocationMultiplier';
import { useWeb3Onboard } from '@src/hooks/useWeb3Onboard';
import { ContractType } from '@src/ts/constants';
import { StakingPool } from '@src/ts/interfaces';
import { getStakingVersions } from '@src/utils/staking';
import { parseBalance } from '@src/utils/web3';

import { useAppDispatch, useAppSelector, useTokenBalance } from '../..';

import { AllocationMultpilerFitter } from './linefitter';
import {
    calculateAmountToStake,
    getAmountToStakeObj,
    getAvailableTiers,
    getCurrentTier,
    getMultiplyAmount,
} from './utils';

export const useAllocationModal = (): IncreaseAllocationModalProps => {
    const {
        _userTier,
        _tiers,
        _requiredDCB,
        contract_manager,
        setShowBuyModal,
        show_allocation_modal,
        setShowAllocationModal,
    } = useGlobalContext();
    const dispatch = useAppDispatch();

    const { user } = useAppSelector((state) => state.auth);

    const { minimum_dcb_power, user_allocation_multiplier } =
        useUserAllocationMultiplier(_tiers, _userTier);
    const [multiplier_value, setMultiplierValue] = useState(1);
    const [tiersToUse, setTiersToUse] = useState<AllocationTier[]>(
        _tiers.map((t) => ({
            ...t,
            alloc_multiplier: t.min_limit / (minimum_dcb_power || 1), // avoid division by zero
        })),
    );

    const [bar_value, setBarValue] = useState(0); // Percentage value (0-100)

    const [is_dragging, setIsDragging] = useState(false);

    const [token_price, setTokenPrice] = useState(0);

    const [amount_token_to_stake, setAmountTokenToStake] = useState(0);

    const [stage, setStage] = useState(IncreaseAllocationStage.AllocationStage);
    const { pools } = useAppSelector((state) => state?.staking);

    const { account, connect } = useWeb3Onboard();

    const filtered_by_version = useMemo(
        () => filterPoolsByStakingVersions(pools, getStakingVersions(false)),
        [pools],
    );

    const is_connected_verified_wallet = user?.wallet_address === account;

    const allocationBarHelper = useMemo(() => {
        const step = 100 / (tiersToUse.length - 1);
        const points = tiersToUse.map((tier, index) => [
            step * index,
            getMultiplyAmount(tier, minimum_dcb_power),
        ]);
        return new AllocationMultpilerFitter(points);
    }, [tiersToUse, minimum_dcb_power]);

    const handleClose = () => {
        setShowAllocationModal(false);
        setStage(IncreaseAllocationStage.AllocationStage);
        setMultiplierValue(user_allocation_multiplier);
        setBarValue(
            allocationBarHelper.multiplierToPercentage(
                user_allocation_multiplier,
            ),
        );
    };

    useEffect(() => {
        if (
            show_allocation_modal &&
            allocationBarHelper &&
            user_allocation_multiplier != null
        ) {
            setMultiplierValue(user_allocation_multiplier);
            setBarValue(
                allocationBarHelper.multiplierToPercentage(
                    user_allocation_multiplier,
                ),
            );
        }
    }, [
        show_allocation_modal,
        user_allocation_multiplier,
        allocationBarHelper,
    ]);

    const badTierInfo = useMemo(
        () =>
            !_tiers?.length ||
            (_tiers[0].min_limit === 0 && minimum_dcb_power === 0),
        [_tiers, minimum_dcb_power],
    );

    useEffect(() => {
        if (tiersToUse.length > 0 && !badTierInfo && minimum_dcb_power) {
            setAmountTokenToStake(
                calculateAmountToStake(
                    user_allocation_multiplier,
                    multiplier_value,
                    minimum_dcb_power,
                    _userTier?.power,
                    _userTier?.actual_amount,
                ),
            );
        }
    }, [
        multiplier_value,
        user_allocation_multiplier,
        tiersToUse,
        _userTier?.power,
        minimum_dcb_power,
        badTierInfo,
    ]);

    // when user allocation multiplier loads make sure to update the multiplier value and bar value
    useEffect(() => {
        if (!user_allocation_multiplier) return;
        setMultiplierValue(user_allocation_multiplier);
        setBarValue(
            allocationBarHelper.multiplierToPercentage(
                user_allocation_multiplier,
            ),
        );
    }, [user_allocation_multiplier, allocationBarHelper]);

    const {
        address: base_token_address,
        symbol: base_token_symbol,
        decimals: base_token_decimals,
    } = CONTRACT.BaseToken[DEFAULT_CHAIN_ID];

    const token_balance_wei = useTokenBalance(
        base_token_address,
        DEFAULT_CHAIN_ID,
        false,
    );

    const has_insufficient_fund = useMemo(() => {
        const token_balance = parseFloat(
            formatUnits(token_balance_wei, base_token_decimals),
        );

        if (!account || amount_token_to_stake === 0) {
            return false;
        }

        return amount_token_to_stake > token_balance;
    }, [amount_token_to_stake, token_balance_wei, account]);

    // Calculate user_current_alloc_weight and its percentage
    const current_allocation_weight_percent = useMemo(() => {
        if (
            badTierInfo ||
            tiersToUse.length === 0 ||
            tiersToUse[0].min_limit === 0
        ) {
            return 0;
        }

        return allocationBarHelper.multiplierToPercentage(
            user_allocation_multiplier,
        );
    }, [
        user_allocation_multiplier,
        tiersToUse,
        badTierInfo,
        allocationBarHelper,
    ]);

    const minBarValue = useMemo(() => {
        return current_allocation_weight_percent;
    }, [current_allocation_weight_percent]);

    useEffect(() => {
        getTokenPrice('DCB').then((price) => setTokenPrice(price));
    }, []);

    useEffect(() => {
        if (badTierInfo) {
            return;
        }

        if (_userTier && _userTier?.id === _tiers.length - 1) {
            // get last tier
            const last_tier = _tiers[_tiers.length - 1];
            // get current multiplier
            const current_multiplier = _userTier.multiplier || 1;

            setTiersToUse(
                Array.from(Array(_tiers.length).keys()).map((i) => {
                    const min_limit =
                        last_tier.min_limit * (current_multiplier + i);

                    return {
                        ...last_tier,
                        min_limit,
                        name: `${last_tier.name}`,
                        alloc_multiplier: min_limit / minimum_dcb_power,
                    };
                }),
            );

            // generate 6 new tiers for the next multipliers
        } else if (_tiers[0].min_limit === 0) {
            setTiersToUse(
                [
                    {
                        ..._tiers[0],
                        min_limit: minimum_dcb_power,
                    },
                    ..._tiers.slice(1),
                ].map((t) => ({
                    ...t,
                    alloc_multiplier: t.min_limit / minimum_dcb_power,
                })),
            );
        } else {
            setTiersToUse(
                _tiers.map((t) => ({
                    ...t,
                    alloc_multiplier: t.min_limit / minimum_dcb_power,
                })),
            );
        }
    }, [_tiers, minimum_dcb_power, badTierInfo, _userTier]);

    const handleDrag = (event: MouseEvent | TouchEvent) => {
        if (!is_dragging) return;

        // Progress Bar
        const progressBar = document.getElementById('progress-bar');
        if (progressBar) {
            const rect = progressBar.getBoundingClientRect();

            let clientX;
            if (event instanceof MouseEvent) {
                clientX = event.clientX;
            } else if (
                event instanceof TouchEvent &&
                event.touches.length > 0
            ) {
                clientX = event.touches[0].clientX;
            } else {
                return;
            }

            const offsetX = clientX - rect.left;
            const width = progressBar.clientWidth;
            let value = offsetX / width;

            value = Math.max(minBarValue / 100, Math.min(value, 1));

            const bar_value = Math.round(value * 100);

            setBarValue(bar_value);
            setMultiplierValue(
                Math.round(
                    allocationBarHelper.percentageToMultiplier(bar_value),
                ),
            );
        }
    };

    useEffect(() => {
        const handleMouseUpOrTouchEnd = () => setIsDragging(false);

        if (is_dragging) {
            document.addEventListener('mousemove', handleDrag);
            document.addEventListener('touchmove', handleDrag, {
                passive: false,
            });
            document.addEventListener('mouseup', handleMouseUpOrTouchEnd);
            document.addEventListener('touchend', handleMouseUpOrTouchEnd);
        } else {
            // Remove listeners when dragging stops
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('touchmove', handleDrag);
            document.removeEventListener('mouseup', handleMouseUpOrTouchEnd);
            document.removeEventListener('touchend', handleMouseUpOrTouchEnd);
        }

        return () => {
            // Cleanup listeners when the component unmounts or dragging state changes
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('touchmove', handleDrag);
            document.removeEventListener('mouseup', handleMouseUpOrTouchEnd);
            document.removeEventListener('touchend', handleMouseUpOrTouchEnd);
        };
    }, [is_dragging]);

    // handles selecting a pool to stake in from the modal
    const handleSelectPoolInModal = useCallback(
        (pool: StakingPool) => {
            // get the base token address for the client on the correct chain i.e DCB, AITECH, SIDUS
            const base_token = contract_manager
                .getContractAddress(ContractType.BaseToken, pool.chain_id)
                .toLowerCase();

            // if the pool is a liquidity pool, we need to set the deposit amount for the correct token
            const is_liquidity = ['legacy-liquidity', 'liquidity'].includes(
                pool.type,
            );
            const setDepositAmountHandler =
                is_liquidity &&
                pool.input_token[1]?.address.toLowerCase() === base_token // need to set second token deposit amount if it is the base token
                    ? setToken1DepositAmount
                    : setDepositAmount;

            // set the deposit amount
            dispatch(setDepositAmountHandler(commify(amount_token_to_stake)));
            // set the current pool id for the staking modal
            dispatch(setCurrentPool(pool?.id));

            handleClose();

            // open the staking modal
            dispatch(setStakingModalOpen(true));

            // if the users wallet is connected, update the pool to get the users stake information if they already have some
            if (account) {
                dispatch(
                    updatePool(pool?.id, pool.type, account, contract_manager),
                );
            }
        },
        [
            dispatch,
            account,
            contract_manager,
            _requiredDCB,
            amount_token_to_stake,
        ],
    );

    const staking_pools: PoolProps[] = useMemo(() => {
        return filtered_by_version
            .filter(({ end_date }) => dayjs(end_date).isAfter(dayjs()))
            .map((pool) => {
                const r = pool.tier_boost / 100;

                // if user is base and has some power for free then we need to account for it in the next calculation for next pool
                const received_boost_for_free =
                    _userTier?.id === 0 && _userTier.power === minimum_dcb_power
                        ? _userTier.power - _userTier.actual_amount
                        : 0;
                // multi a user receives for free
                const multi_for_free =
                    received_boost_for_free / minimum_dcb_power;
                // multi a user receives from the pool before accounting for the free multi
                const pool_multi_with_boost =
                    (amount_token_to_stake / minimum_dcb_power) * (1 + r);
                // actual mulitplier a user receives for the stake in the pool
                const multiplier_from_pool =
                    pool_multi_with_boost - multi_for_free;
                // final users multiplier after the stake
                const multiplier_with_boost =
                    user_allocation_multiplier + multiplier_from_pool;

                return {
                    ...getPoolItem(pool, dispatch),
                    handlePoolClick: () => handleSelectPoolInModal(pool),
                    user_final_allocation_weight: `${multiplier_with_boost.toFixed(
                        1,
                    )}x`,
                };
            });
    }, [
        filtered_by_version,
        dispatch,
        handleSelectPoolInModal,
        amount_token_to_stake,
        minimum_dcb_power,
        user_allocation_multiplier,
    ]);

    const handleIncrement = () => {
        const nextMultiplier = Math.round(multiplier_value + 1);

        const nextBarValue =
            allocationBarHelper.multiplierToPercentage(nextMultiplier);

        setMultiplierValue(nextMultiplier);
        setBarValue(Math.round(Math.min(nextBarValue, 100)));
    };

    const handleDecrement = () => {
        const prevMultiplier = Math.round(multiplier_value - 1);
        const minMultiplier = user_allocation_multiplier;

        if (prevMultiplier >= minMultiplier) {
            const prevBarValue =
                allocationBarHelper.multiplierToPercentage(prevMultiplier);

            setMultiplierValue(prevMultiplier);
            setBarValue(Math.round(Math.min(prevBarValue, 100)));
        }
    };

    const currentTier = useMemo(
        () => getCurrentTier(_userTier?.power || 0, tiersToUse),
        [tiersToUse, _userTier],
    );
    const availableTiers = useMemo(
        () => getAvailableTiers(currentTier, tiersToUse),
        [currentTier, tiersToUse],
    );

    return {
        button_disabled:
            !account ||
            Math.round(multiplier_value) ===
                Math.round(user_allocation_multiplier) ||
            has_insufficient_fund ||
            !is_connected_verified_wallet,
        onClose: () => {
            handleClose();
        },
        handleConnectWallet:
            account && is_connected_verified_wallet
                ? undefined
                : () => {
                      setShowAllocationModal(false);
                      connect().then(() => setShowAllocationModal(true));
                  },
        amount_to_stake: getAmountToStakeObj(
            amount_token_to_stake || 0,
            token_price || 0,
        ),
        multiplier_value: Math.round(multiplier_value),
        currentTier,
        tiersToUse,
        availableTiers,
        user_current_alloc_weight: user_allocation_multiplier,
        balance: parseBalance(token_balance_wei, base_token_decimals),
        token_symbol: base_token_symbol,
        staking_pools,
        handleOpenBuyModal: () => {
            setShowAllocationModal(false);
            setShowBuyModal(true);
        },
        setIsDragging,
        handleIncrement,
        handleDecrement,
        bar_value,
        minBarValue,
        current_allocation_weight_percent,
        insufficient_fund: {
            has_insufficient_fund,
            tooltip: 'Insufficient funds',
        },
        stage,
        setStage,
        show: show_allocation_modal,
        setShow: setShowAllocationModal,
    };
};
